import {
    DOMConversionMap,
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    LexicalUpdateJSON,
    NodeKey,
    SerializedElementNode,
    Spread,
} from 'lexical';
import {
    $createTableNode,
    type SerializedTableNode,
    TableNode,
} from '@lexical/table';
import { RawHtmlTableStyle } from './tableStyle';

export type SerializedRawHtmlTableNode = Spread<{
    rawHtmlTableStyle?: RawHtmlTableStyle;
}, SerializedTableNode>;

function applyAlignmentStyle(element: HTMLElement, alignment?: RawHtmlTableStyle['alignment'])
{
    element.style.removeProperty('margin-left');
    element.style.removeProperty('margin-right');

    if (alignment === 'left')
    {
        element.style.marginLeft = '0';
        element.style.marginRight = 'auto';
    }
    else if (alignment === 'center')
    {
        element.style.marginLeft = 'auto';
        element.style.marginRight = 'auto';
    }
    else if (alignment === 'right')
    {
        element.style.marginLeft = 'auto';
        element.style.marginRight = '0';
    }
}

function inferAlignmentFromMargins(element: HTMLElement): RawHtmlTableStyle['alignment'] | undefined
{
    const marginLeft = element.style.marginLeft?.trim().toLowerCase();
    const marginRight = element.style.marginRight?.trim().toLowerCase();

    if (marginLeft === 'auto' && marginRight === 'auto') { return 'center'; }
    if (marginLeft === 'auto' && marginRight !== 'auto') { return 'right'; }
    if (marginRight === 'auto' && marginLeft !== 'auto') { return 'left'; }

    // eslint-disable-next-line no-undefined
    return undefined;
}

function normalizeAlignmentFromAttribute(value: string | null): RawHtmlTableStyle['alignment'] | undefined
{
    const normalized = value?.trim().toLowerCase();

    if (normalized === 'left' || normalized === 'center' || normalized === 'right')
    {
        return normalized;
    }

    // eslint-disable-next-line no-undefined
    return undefined;
}

function applyTableStyle(element: HTMLElement, style?: RawHtmlTableStyle) {
    applyAlignmentStyle(element, style?.alignment);

    if (!style) { return; }

    if (style.borderCollapse) {
        element.style.borderCollapse = style.borderCollapse;
    }

    if (style.borderSpacing) {
        element.style.borderSpacing = style.borderSpacing;
    }

    if (style.width) {
        element.style.width = style.width;
    }

    if (style.tableLayout) {
        element.style.tableLayout = style.tableLayout;
    }
}

function readTableStyle(element: HTMLElement): RawHtmlTableStyle
{
    const style: RawHtmlTableStyle = {};
    const { borderCollapse, tableLayout } = element.style;
    const alignmentFromAttribute = normalizeAlignmentFromAttribute(element.getAttribute('align'));
    const alignmentFromMargins = inferAlignmentFromMargins(element);

    if (borderCollapse === 'collapse' || borderCollapse === 'separate')
    {
        style.borderCollapse = borderCollapse;
    }

    if (element.style.borderSpacing)
    {
        style.borderSpacing = element.style.borderSpacing;
    }

    if (element.style.width)
    {
        style.width = element.style.width;
    }

    if (tableLayout === 'auto' || tableLayout === 'fixed')
    {
        style.tableLayout = tableLayout;
    }

    style.alignment = alignmentFromAttribute ?? alignmentFromMargins;

    return style;
}

function updateTableAlignAttribute(element: HTMLElement, alignment?: RawHtmlTableStyle['alignment'])
{
    if (alignment)
    {
        element.setAttribute('align', alignment);
    }
    else
    {
        element.removeAttribute('align');
    }
}

export class RawHtmlTableNode extends TableNode
{
    __rawHtmlTableStyle?: RawHtmlTableStyle;

    static importJSON(serializedNode: SerializedRawHtmlTableNode): RawHtmlTableNode
    {
        return $createRawHtmlTableNode().updateFromJSON(serializedNode);
    }

    static importDOM(): DOMConversionMap | null
    {
        const base = TableNode.importDOM?.() ?? {};

        return {
            ...base,
            table: () => (
            {
                priority: 4,
                conversion: element =>
                {
                    const table = element as HTMLTableElement;
                    const output = base.table?.(table)?.conversion(table);
                    const node = $createRawHtmlTableNode(readTableStyle(table));
                    return { ...output, node };
                },
            }),
        };
    }

    static getType(): string
    {
        return 'raw-html-table';
    }

    static clone(node: RawHtmlTableNode): RawHtmlTableNode
    {
        return new RawHtmlTableNode(node.__rawHtmlTableStyle, node.__key);
    }

    constructor(style?: RawHtmlTableStyle, key?: NodeKey)
    {
        super(key);
        this.__rawHtmlTableStyle = style;
    }

    createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement
    {
        const element = super.createDOM(config, editor);
        const table = element instanceof HTMLTableElement
            ? element
            : element.querySelector('table');
        if (table)
        {
            applyTableStyle(table, this.__rawHtmlTableStyle);
            updateTableAlignAttribute(table, this.__rawHtmlTableStyle?.alignment);
        }
        return element;
    }

    updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean
    {
        const result = super.updateDOM(prevNode, dom, config);

        const table = dom instanceof HTMLTableElement
            ? dom
            : dom.querySelector('table');

        if (table && prevNode.__rawHtmlTableStyle !== this.__rawHtmlTableStyle)
        {
            table.removeAttribute('style');
            applyTableStyle(table, this.__rawHtmlTableStyle);
            updateTableAlignAttribute(table, this.__rawHtmlTableStyle?.alignment);
        }

        return result;
    }

    exportDOM(editor: LexicalEditor): DOMExportOutput
    {
        const output = super.exportDOM(editor);

        return {
            ...output,
            after: element => {
                const resolved = output.after ? output.after(element) : element;

                if (resolved instanceof HTMLTableElement) {
                    applyTableStyle(resolved, this.__rawHtmlTableStyle);
                    updateTableAlignAttribute(resolved, this.__rawHtmlTableStyle?.alignment);
                }

                return resolved;
            }
        };
    }

    exportJSON(): SerializedRawHtmlTableNode
    {
        return {
            ...super.exportJSON(),
            type: 'raw-html-table',
            rawHtmlTableStyle: this.__rawHtmlTableStyle,
        };
    }

    updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedRawHtmlTableNode>): this
    {
        return super
            .updateFromJSON(serializedNode)
            .setRawHtmlTableStyle(serializedNode.rawHtmlTableStyle ?? null);
    }

    setRawHtmlTableStyle(style: RawHtmlTableStyle | null): this
    {
        const self = this.getWritable();
        self.__rawHtmlTableStyle = style ?? void 0;
        return self;
    }

    getRawHtmlTableStyle(): RawHtmlTableStyle | undefined
    {
        return this.getLatest().__rawHtmlTableStyle;
    }
}

export function $createRawHtmlTableNode(style?: RawHtmlTableStyle): RawHtmlTableNode
{
    return new RawHtmlTableNode(style);
}

export function $isRawHtmlTableNode(node: LexicalNode | null | undefined): node is RawHtmlTableNode
{
    return node instanceof RawHtmlTableNode;
}